import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import api from '../services/apiService';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';

const NotificationContext = createContext(null);

/**
 * ToastItem — small app-level toast for success/error/info feedback messages.
 * Distinct from NotificationToast, which shows real-time notification pop-ups.
 */
function ToastItem({ toast, onDismiss }) {
  const colors = {
    success: { bg: '#2E7D32', text: '#ffffff' },
    error: { bg: '#EF4444', text: '#ffffff' },
    warning: { bg: '#FF9800', text: '#ffffff' },
    info: { bg: '#2196F3', text: '#ffffff' },
  };
  const c = colors[toast.type] ?? colors.info;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg max-w-sm w-full cursor-pointer"
      style={{ backgroundColor: c.bg, color: c.text }}
      onClick={() => onDismiss(toast.id)}
    >
      <div className="flex-1 text-sm font-medium">{toast.message}</div>
      <button
        className="opacity-70 hover:opacity-100 text-lg leading-none"
        onClick={(e) => { e.stopPropagation(); onDismiss(toast.id); }}
      >
        ×
      </button>
    </motion.div>
  );
}

export function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const socket = useSocket();

  const [notifications, setNotifications] = useState([]);
  // unreadCount is tracked separately so socket updates stay in sync
  const [unreadCount, setUnreadCount] = useState(0);
  // newNotification holds the latest real-time notification for the popup toast
  const [newNotification, setNewNotification] = useState(null);

  // App-level toasts (success/error/warning/info feedback messages)
  const [toasts, setToasts] = useState([]);
  const toastCounter = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const { data } = await api.get('/notifications');
      const list = Array.isArray(data) ? data : data.notifications ?? data.data ?? [];
      setNotifications(list);
      // Derive unread count from fetched list
      setUnreadCount(list.filter((n) => !n.is_read && !n.read).length);
    } catch {
      // silent fail
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // --- Socket.IO real-time listeners ---
  useEffect(() => {
    if (!socket) return;

    // New notification arrives: prepend to list, show popup, increment badge
    const handleNewNotification = (notification) => {
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
      setNewNotification(notification);
    };

    // Server pushes authoritative unread count (after read/readAll)
    const handleUnreadCount = ({ count }) => {
      setUnreadCount(count ?? 0);
    };

    // Re-fetch notifications when a new message arrives (belt-and-suspenders:
    // the notification:new event may be gated by notification_preferences,
    // but new_message is always emitted by messageController).
    const handleNewMessage = () => fetchNotifications();

    socket.on('notification:new', handleNewNotification);
    socket.on('notification:unread_count', handleUnreadCount);
    socket.on('new_message', handleNewMessage);

    return () => {
      socket.off('notification:new', handleNewNotification);
      socket.off('notification:unread_count', handleUnreadCount);
      socket.off('new_message', handleNewMessage);
    };
  }, [socket, fetchNotifications]);

  const markAsRead = useCallback(async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      // Update state only after API call succeeds — no rollback needed
      setNotifications((prev) =>
        prev.map((n) => (n._id === id || n.id === id ? { ...n, is_read: true, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silent fail — state remains unchanged since we update after success
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
    } catch {
      // silent fail
    }
  }, []);

  /** Clear the real-time notification popup toast. */
  const clearToast = useCallback(() => {
    setNewNotification(null);
  }, []);

  /** Show an app-level feedback toast (success/error/warning/info). */
  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastCounter.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = {
    notifications,
    unreadCount,
    newNotification,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    showToast,
    clearToast,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* App-level feedback toasts (bottom-right, above notification bell toast) */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        <AnimatePresence>
          {toasts.map((t) => (
            <div key={t.id} className="pointer-events-auto">
              <ToastItem toast={t} onDismiss={dismissToast} />
            </div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
